import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Heading2, Heading3, Pilcrow, List, ListOrdered, Link2, Undo2, Redo2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const TBtn = ({ on, active, label, icon: Icon, testid, disabled }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} disabled={disabled} title={label} aria-label={label}
    className={`h-9 min-w-9 px-2 grid place-items-center border text-sm transition-colors ${active ? "border-[var(--xd-red)] bg-[var(--xd-red)]/15 text-white" : "border-white/10 text-zinc-300 hover:bg-white/5"} disabled:opacity-30`}
    data-testid={testid}><Icon size={16} /></button>
);

const Toolbar = ({ editor }) => {
  const { t } = useLang();
  const c = () => editor.chain().focus();
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt(t("pe.link_prompt"), prev);
    if (url === null) return;
    if (!url.trim()) { c().extendMarkRange("link").unsetLink().run(); return; }
    if (!/^(https?:\/\/|\/|mailto:|tel:)/i.test(url.trim())) return;
    c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const B = [
    ["h2", Heading2, () => c().toggleHeading({ level: 2 }).run(), editor.isActive("heading", { level: 2 })],
    ["h3", Heading3, () => c().toggleHeading({ level: 3 }).run(), editor.isActive("heading", { level: 3 })],
    ["p", Pilcrow, () => c().setParagraph().run(), editor.isActive("paragraph")],
    ["bold", Bold, () => c().toggleBold().run(), editor.isActive("bold")],
    ["italic", Italic, () => c().toggleItalic().run(), editor.isActive("italic")],
    ["ul", List, () => c().toggleBulletList().run(), editor.isActive("bulletList")],
    ["ol", ListOrdered, () => c().toggleOrderedList().run(), editor.isActive("orderedList")],
    ["link", Link2, link, editor.isActive("link")],
  ];
  return (
    <div className="sticky top-0 z-10 flex flex-wrap gap-1 p-2 border-b border-white/10 bg-[#0b0b0b]" data-testid="rich-toolbar">
      {B.map(([k, icon, on, active]) => <TBtn key={k} on={on} active={active} label={t(`pe.${k}`)} icon={icon} testid={`rich-btn-${k}`} />)}
      <span className="w-px bg-white/10 mx-1" />
      <TBtn on={() => c().undo().run()} disabled={!editor.can().undo()} label={t("pe.undo")} icon={Undo2} testid="rich-btn-undo" />
      <TBtn on={() => c().redo().run()} disabled={!editor.can().redo()} label={t("pe.redo")} icon={Redo2} testid="rich-btn-redo" />
    </div>
  );
};

export const RichEditor = ({ html, dir, onChange, onReady, editable = true }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: { openOnClick: false, autolink: true } })],
    content: html,
    editable,
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "xd-prose ProseMirror min-h-[420px] p-4 md:p-6 focus:outline-none", dir, "data-testid": "rich-editor-content" } },
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
  });
  useEffect(() => { if (editor) { onReady?.(editor); } }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!editor) return null;
  return (
    <div className="border border-white/10 bg-black/40 max-h-[70vh] overflow-y-auto" data-testid="rich-editor">
      {editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
};
